import { useRef, type ReactNode } from "react";
import { Dialog as DialogRadix } from "radix-ui";
import IconButton from "../IconButton/IconButton";
import { text as textStyles } from "../Text/Text.css";
import styles from "./Dialog.css";

const FIELD_SELECTOR =
  "input:not([type=hidden]):not([disabled]), textarea:not([disabled])";
const FOOTER_BUTTON_SELECTOR = "button:not([disabled]), a[href]";

type DialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  closeAriaLabel: string;
  size?: "medium" | "large";
  /** Extra content below the description — a QR code, a copyable link, etc. */
  contentSlot?: ReactNode;
  footer?: ReactNode;
};
const Dialog = ({
  open,
  onOpenChange,
  title,
  description,
  closeAriaLabel,
  size = "medium",
  contentSlot,
  footer,
}: DialogProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);

  // Radix focuses the first tabbable element on open, which here is always the
  // header's close button. Mirror the legacy Dialog instead: the first field so
  // the user can start typing, otherwise the primary action — the footer's
  // last button. Falls back to Radix's default when there's neither.
  const handleOpenAutoFocus = (event: Event) => {
    const field = contentRef.current?.querySelector<
      HTMLInputElement | HTMLTextAreaElement
    >(FIELD_SELECTOR);
    const footerButtons = footerRef.current?.querySelectorAll<HTMLElement>(
      FOOTER_BUTTON_SELECTOR,
    );
    const target = field ?? footerButtons?.[footerButtons.length - 1];
    if (!target) return;

    event.preventDefault();
    target.focus({ preventScroll: true });
    // A prefilled value (e.g. the name being renamed) gets replaced by typing.
    field?.select();
  };

  return (
    <DialogRadix.Root open={open} onOpenChange={onOpenChange}>
      <DialogRadix.Portal>
        <DialogRadix.Overlay className={styles.overlay} />
        <DialogRadix.Content
          ref={contentRef}
          className={styles.content({ size })}
          onOpenAutoFocus={handleOpenAutoFocus}
        >
          <div className={styles.header}>
            <DialogRadix.Title
              className={textStyles({
                font: "subheader-small",
                color: "neutral-0",
              })}
            >
              {title}
            </DialogRadix.Title>
            <DialogRadix.Close asChild>
              <IconButton
                icon="x"
                ariaLabel={closeAriaLabel}
                size="small"
                color="neutral"
                variant="tertiary"
              />
            </DialogRadix.Close>
          </div>

          <DialogRadix.Description
            className={textStyles({ font: "body", color: "neutral-100" })}
          >
            {description}
          </DialogRadix.Description>

          {contentSlot}

          {footer && (
            <div ref={footerRef} className={styles.footer}>
              {footer}
            </div>
          )}
        </DialogRadix.Content>
      </DialogRadix.Portal>
    </DialogRadix.Root>
  );
};

export default Dialog;
